import { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';

interface EmptyStateAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  description: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  /**
   * Versão baixa, sem o círculo do ícone, para quando o vazio substitui um
   * pedaço da tela e não a tela: o gráfico dentro do card, a lista de uma aba.
   * O bloco cheio (`py-16` + círculo de 64px) empurraria o resto do card para
   * fora da dobra — e foi por isso que essas telas desenharam o vazio à mão em
   * vez de usar o kit.
   */
  compact?: boolean;
}

function ActionButton({ action, primary }: { action: EmptyStateAction; primary: boolean }) {
  const baseClass = primary
    ? 'inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90'
    : 'text-sm font-medium text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline';

  if (action.href) {
    return (
      <Link to={action.href} className={baseClass}>
        {action.label}
      </Link>
    );
  }

  return (
    <button type="button" onClick={action.onClick} className={baseClass}>
      {action.label}
    </button>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  compact = false,
}: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={
        compact
          ? 'flex flex-col items-center justify-center py-8'
          : 'flex flex-col items-center justify-center py-16'
      }
    >
      {compact ? (
        <div className="text-muted-foreground">{icon}</div>
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {icon}
        </div>
      )}
      <h3
        className={
          compact
            ? 'mt-2 text-sm font-semibold text-foreground'
            : 'mt-4 text-lg font-semibold text-foreground'
        }
      >
        {title}
      </h3>
      <p
        className={
          compact
            ? 'mt-0.5 max-w-sm text-center text-xs text-muted-foreground'
            : 'mt-1 max-w-sm text-center text-sm text-muted-foreground'
        }
      >
        {description}
      </p>
      {action && (
        <div className="mt-5">
          <ActionButton action={action} primary />
        </div>
      )}
      {secondaryAction && (
        <div className="mt-2">
          <ActionButton action={secondaryAction} primary={false} />
        </div>
      )}
    </motion.div>
  );
}
